import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import {
  CheckCircle, Circle, ArrowRight, ArrowLeft, Calculator, BookOpen,
  Wrench, Box, Brain, Clock, FileQuestion, Zap,
} from 'lucide-react';

const ICON_MAP = { calculator: Calculator, 'book-open': BookOpen, wrench: Wrench, box: Box, brain: Brain };

const Assessments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTest, setActiveTest] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [quickRunning, setQuickRunning] = useState(false);

  const fetchPlan = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/testing/plan/${user.id}`);
      setPlan(res.data);
    } catch (err) {
      console.error('Failed to fetch testing plan:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const startTest = async (testId) => {
    try {
      const res = await axios.get(`${API_BASE}/testing/questions/${testId}`);
      setActiveTest({ id: testId, title: res.data.title, questions: res.data.questions });
      setCurrentQ(0);
      setAnswers({});
      setTestResult(null);
    } catch (err) {
      toast.error('Failed to load test');
    }
  };

  const submitTest = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/testing/submit/${activeTest.id}`, {
        user_id: user.id,
        answers: answers,
      });
      setTestResult(res.data);
    } catch (err) {
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const exitTest = () => {
    setActiveTest(null);
    setTestResult(null);
    setAnswers({});
    fetchPlan();
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;
  }

  // =========== TEST RESULT VIEW ===========
  if (testResult) {
    const passed = testResult.score >= 60;
    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${passed ? 'bg-green-100' : 'bg-orange-100'}`}>
            <CheckCircle className={`w-10 h-10 ${passed ? 'text-green-600' : 'text-orange-500'}`} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-2" data-testid="test-result-title">
            {activeTest.title}
          </h1>
          <p className="text-lg text-[#86868b] mb-8">{passed ? 'Great job!' : 'Keep practising!'}</p>
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-8 mb-6">
            <div className="text-6xl font-bold mb-2" style={{ color: passed ? '#34C759' : '#FF9500' }} data-testid="test-score">
              {testResult.score}%
            </div>
            <p className="text-sm text-[#86868b]">{testResult.correct} of {testResult.total} correct</p>
          </div>
          <Button onClick={exitTest} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8" data-testid="back-to-tests-btn">
            Back to Tests <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // =========== ACTIVE TEST VIEW ===========
  if (activeTest) {
    const q = activeTest.questions[currentQ];
    const totalQ = activeTest.questions.length;
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <button onClick={exitTest} className="text-sm text-[#86868b] hover:text-[#1d1d1f] mb-3 flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to test plan
            </button>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f]" data-testid="active-test-title">{activeTest.title}</h1>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-[#86868b] mb-2">
              <span>Question {currentQ + 1} of {totalQ}</span>
              <span>{answeredCount} answered</span>
            </div>
            <div className="w-full h-1.5 bg-[#f5f5f7] rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-[#0071e3] transition-all duration-500" style={{ width: `${((currentQ + 1) / totalQ) * 100}%` }} />
            </div>
          </div>

          {/* Question */}
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 mb-6">
            <h3 className="text-lg sm:text-xl font-semibold text-[#1d1d1f] mb-6" data-testid="test-question-text">{q.question}</h3>
            <div className="space-y-3">
              {q.options.map((option, idx) => {
                const isSelected = answers[q.id] === option;
                return (
                  <button
                    key={idx}
                    onClick={() => setAnswers(prev => ({ ...prev, [q.id]: option }))}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-[#0071e3] bg-blue-50 text-[#1d1d1f]'
                        : 'border-[#d2d2d7] bg-white text-[#1d1d1f] hover:border-[#86868b]'
                    }`}
                    data-testid={`test-option-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-[#0071e3] bg-[#0071e3]' : 'border-[#d2d2d7]'
                      }`}>
                        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <span className="text-sm sm:text-base">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0} variant="ghost" className="rounded-full text-[#86868b]" data-testid="test-prev-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            {currentQ < totalQ - 1 ? (
              <Button onClick={() => setCurrentQ(currentQ + 1)} disabled={!answers[q.id]} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-6" data-testid="test-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submitTest} disabled={answeredCount < totalQ || submitting} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-6" data-testid="test-submit-btn">
                {submitting ? 'Submitting...' : 'Submit Test'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========== TEST PLAN DASHBOARD ===========
  if (!plan) return null;

  const completedCount = plan.completed || 0;
  const totalTests = plan.total || 5;
  const allComplete = plan.all_complete;

  // Find next incomplete test
  const nextTest = plan.tests?.find(t => !t.completed);

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Quick Complete Banner */}
        <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="testing-demo-banner">
          <div className="text-sm text-[#1d1d1f]">
            <span className="font-semibold">Investor Demo:</span> Skip to see all tests completed
          </div>
          <Button
            onClick={async () => {
              setQuickRunning(true);
              try {
                await axios.post(`${API_BASE}/demo/quick-complete-testing/${user.id}`);
                toast.success('All aptitude tests auto-completed!');
                await fetchPlan();
              } catch (err) { toast.error('Quick complete failed'); }
              finally { setQuickRunning(false); }
            }}
            disabled={quickRunning}
            className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-5 text-sm"
            data-testid="quick-complete-testing-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {quickRunning ? 'Completing...' : 'Quick Complete All'}
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="testing-title">
            Aptitude Tests
          </h1>
          <p className="text-lg text-[#86868b]">
            Complete 5 general aptitude tests. Each test has 25 questions.
          </p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h2 className="text-xl font-semibold text-[#1d1d1f]">Your Progress</h2>
            <span className="text-sm text-[#86868b]">{completedCount} of {totalTests} tests completed</span>
          </div>
          <div className="w-full h-2 bg-[#f5f5f7] rounded-full overflow-hidden">
            <div className="h-full bg-[#34C759] rounded-full transition-all duration-700" style={{ width: `${(completedCount / totalTests) * 100}%` }} />
          </div>
        </div>

        {/* Test Cards — Sequential */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {(plan.tests || []).map((test, idx) => {
            const Icon = ICON_MAP[test.icon] || Brain;
            const isNext = nextTest?.test_id === test.test_id;
            const isLocked = !test.completed && !isNext && idx > completedCount;

            return (
              <div
                key={test.test_id}
                className={`bg-white rounded-2xl border p-5 transition-all ${
                  test.completed ? 'border-green-300' : isNext ? 'border-[#0071e3] shadow-sm' : 'border-[#d2d2d7]'
                } ${isLocked ? 'opacity-50' : ''}`}
                data-testid={`test-card-${test.test_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    test.completed ? 'bg-green-100' : isNext ? 'bg-blue-100' : 'bg-[#f5f5f7]'
                  }`}>
                    {test.completed ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <Icon className={`w-6 h-6 ${isNext ? 'text-[#0071e3]' : 'text-[#86868b]'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#86868b] font-medium">Test {idx + 1}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#1d1d1f]">{test.title}</h3>
                    <p className="text-sm text-[#86868b]">{test.description}</p>
                    <div className="flex items-center gap-3 text-xs text-[#86868b] mt-1">
                      <span className="flex items-center gap-1"><FileQuestion className="w-3 h-3" /> {test.question_count} questions</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {test.duration}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {test.completed ? (
                      <div className="text-right">
                        <span className="text-lg font-bold text-green-600">{test.score}%</span>
                        {test.band && <p className="text-xs font-semibold mt-0.5" style={{ color: test.band === 'Exceptional' ? '#34C759' : test.band === 'Strong' ? '#0071e3' : test.band === 'Competent' ? '#FF9500' : '#FF3B30' }}>{test.band}</p>}
                        <div className="flex gap-1 mt-1">
                          <Button onClick={() => startTest(test.test_id)} variant="ghost" className="text-xs text-[#0071e3] h-7 px-2 rounded-full" data-testid={`retake-${test.test_id}`}>
                            Retake
                          </Button>
                        </div>
                      </div>
                    ) : isNext ? (
                      <div className="flex gap-2">
                        <Button onClick={() => startTest(test.test_id)} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full h-9 px-5 text-sm" data-testid={`start-${test.test_id}`}>
                          Start <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                        <Button
                          onClick={async () => {
                            try {
                              await axios.post(`${API_BASE}/demo/quick-complete-single-test/${user.id}/${test.test_id}`);
                              toast.success(`${test.title} auto-completed!`);
                              await fetchPlan();
                            } catch (err) { toast.error('Quick complete failed'); }
                          }}
                          className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full h-9 px-3 text-xs"
                          data-testid={`quick-${test.test_id}`}
                        >
                          <Zap className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Circle className="w-6 h-6 text-[#d2d2d7]" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue button when all complete */}
        {allComplete && (
          <div className="mt-8 text-center">
            <Button
              onClick={() => navigate('/apprentice/booking')}
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8 h-12 text-base"
              data-testid="continue-to-booking-btn"
            >
              Continue to Counsellor Booking <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Assessments;
